/* eslint-disable */
import {
	FieldTemplateProps,
	FormContextType,
	GenericObjectType,
	getTemplate,
	getUiOptions,
	RJSFSchema,
	StrictRJSFSchema,
} from '@rjsf/utils';
import clsx from 'clsx';
import React from 'react';

import Tooltip from '@/components/core/Tooltip';
import { Label } from '@/ui/Label';

import styles from './style.module.sass';
import { TutorialIcon } from '@/features/tutorial/views/TutorialIcon';

const className = 'flex flex-col gap-y-1 flex-1';

function FieldTemplate<T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
	props: FieldTemplateProps<T & { fieldNameInValidationSchema: string }, S & { hidden: boolean }, F>
) {
	const {
		children,
		classNames,
		style,
		description,
		disabled,
		displayLabel,
		errors,
		formContext,
		help,
		hidden,
		id,
		label,
		onDropPropertyClick,
		onKeyChange,
		rawErrors,
		rawDescription,
		rawHelp,
		readonly,
		registry,
		required,
		schema,
		uiSchema,
		formData
	} = props;
	const { descriptionLocation = 'below' } = formContext as GenericObjectType;

	let fieldName = id;
	if (id.startsWith('root_')) {
		fieldName = id.replace('root_', '');
	}

	const uiOptions = getUiOptions<T, S, F>(uiSchema);
	const WrapIfAdditionalTemplate = getTemplate<'WrapIfAdditionalTemplate', T, S, F>(
		'WrapIfAdditionalTemplate',
		registry,
		uiOptions
	);

	if (hidden || schema?.hidden) {
		return <div className="hidden">{children}</div>;
	}

	const descriptionNode = formContext?.schemaProperties?.[formData?.fieldNameInValidationSchema]?.note || schema.description || rawDescription || undefined;
	const isAny = /field-undefined/gi.test(classNames);
	const isDisplayLabel = id.toLowerCase() !== label.toLowerCase()
	const uiLabel = uiSchema?.['ui:label'] ?? true;

	return (
		<Tooltip
			description={
				descriptionLocation === 'tooltip'
					? descriptionNode
					: undefined
			}
			config={{
				trigger: ['hover'],
			}}
		>
			<WrapIfAdditionalTemplate
				classNames={classNames}
				style={style}
				disabled={disabled}
				id={id}
				label={label}
				onDropPropertyClick={onDropPropertyClick}
				onKeyChange={onKeyChange}
				readonly={readonly}
				required={required}
				schema={schema}
				uiSchema={uiSchema}
				registry={registry}
			>
				<div
					data-role="FieldTemplate"
					className={clsx(
						props.uiSchema?.props?.className ?? className,
						{
							'any-field': isAny,
						},
						styles['any-field']
					)}
				>
					{uiLabel && label && !isAny && isDisplayLabel && (
						<Label htmlFor={id}>
							{label} {required ? <span className="text-red-500">*</span> : null}
							{formContext?.tutorialsFields?.[fieldName] && (
								<TutorialIcon tutorial={formContext.tutorialsFields[fieldName]}/>
							)}
						</Label>
					)}
					<div className={clsx('flex flex-col gap-y-1')}>
						{children}

						{errors}

						{descriptionLocation === 'below' && descriptionNode?.trim() &&
							<div
								className={clsx(styles.description, props.uiSchema?.props?.descriptionClassName)}
								dangerouslySetInnerHTML={{ __html: descriptionNode.trim() }}
							/>
						}
					</div>
				</div>
				<style jsx global>
					{`
						.form-group {
							margin-bottom: 16px;
						}
					`}
				</style>
			</WrapIfAdditionalTemplate>
		</Tooltip>
	);
}

export { FieldTemplate };
